'use client';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import PinDropIcon from '@mui/icons-material/PinDrop';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import SignpostIcon from '@mui/icons-material/Signpost';
import { FormField } from './FormField';
import styles from './ProfileEditForm.module.css';

interface ProfileAddress {
	zipCode?: string;
	state?: string;
	city?: string;
	district?: string;
	street?: string;
	number?: string;
}

interface ProfileFormData {
	name: string;
	surName: string;
	email: string;
	phoneNumber?: string;
	document?: string;
	address: ProfileAddress;
}

interface ProfileEditFormProps {
	formData: ProfileFormData;
	errors: Record<string, string | undefined>;
	touched: Record<string, boolean | undefined>;
	isLoading?: boolean;
	onChange: (field: string, value: string) => void;
	onBlur: (field: string) => void;
}

export const ProfileEditForm = ({
	formData,
	errors,
	touched,
	isLoading,
	onChange,
	onBlur,
}: ProfileEditFormProps) => {
	return (
		<div className={styles.card}>
			<div className={styles.cardTitle}>
				<CreditCardRoundedIcon className={styles.badgeIcon} />
				<h3 className={styles.titleText}>Informações Pessoais</h3>
			</div>

			<div className={styles.formContent}>
				<div className={styles.grid}>
					<FormField
						label="Nome"
						icon={<PersonIcon />}
						value={formData.name}
						onChange={(value) => onChange('name', value)}
						onBlur={() => onBlur('name')}
						error={errors.name}
						touched={touched.name}
						disabled={isLoading}
					/>

					<FormField
						label="Sobrenome"
						icon={<PersonIcon />}
						value={formData.surName}
						onChange={(value) => onChange('surName', value)}
						onBlur={() => onBlur('surName')}
						error={errors.surName}
						touched={touched.surName}
						disabled={isLoading}
					/>

					<FormField
						label="E-mail"
						icon={<EmailIcon />}
						type="email"
						value={formData.email}
						onChange={(value) => onChange('email', value)}
						onBlur={() => onBlur('email')}
						error={errors.email}
						touched={touched.email}
						disabled={isLoading}
					/>

					<FormField
						label="Telefone"
						icon={<PhoneIcon />}
						value={formData.phoneNumber ?? ''}
						onChange={(value) => onChange('phoneNumber', value)}
						onBlur={() => onBlur('phoneNumber')}
						error={errors.phoneNumber}
						touched={touched.phoneNumber}
						placeholder="(00) 00000-0000"
						maxLength={15}
						disabled={isLoading}
					/>

					<FormField
						label="CPF/CNPJ"
						icon={<FingerprintIcon />}
						value={formData.document ?? ''}
						onChange={(value) => onChange('document', value)}
						onBlur={() => onBlur('document')}
						error={errors.document}
						touched={touched.document}
						placeholder="000.000.000-00 ou 00.000.000/0000-00"
						maxLength={18}
						disabled={isLoading}
					/>

					<FormField
						label="CEP"
						icon={<PinDropIcon />}
						value={formData.address.zipCode ?? ''}
						onChange={(value) => onChange('address.zipCode', value)}
						onBlur={() => onBlur('address.zipCode')}
						error={errors['address.zipCode']}
						touched={touched['address.zipCode']}
						placeholder="00000-000"
						maxLength={9}
						disabled={isLoading}
					/>

					<FormField
						label="Estado"
						icon={<LocationCityIcon />}
						value={formData.address.state ?? ''}
						onChange={(value) => onChange('address.state', value)}
						onBlur={() => onBlur('address.state')}
						placeholder="GO"
						maxLength={2}
						disabled={isLoading}
					/>

					<FormField
						label="Cidade"
						icon={<LocationCityIcon />}
						value={formData.address.city ?? ''}
						onChange={(value) => onChange('address.city', value)}
						onBlur={() => onBlur('address.city')}
						error={errors['address.city']}
						touched={touched['address.city']}
						disabled={isLoading}
					/>

					<FormField
						label="Bairro"
						icon={<LocationCityIcon />}
						value={formData.address.district ?? ''}
						onChange={(value) => onChange('address.district', value)}
						onBlur={() => onBlur('address.district')}
						disabled={isLoading}
					/>

					<FormField
						label="Rua"
						icon={<SignpostIcon />}
						value={formData.address.street ?? ''}
						onChange={(value) => onChange('address.street', value)}
						onBlur={() => onBlur('address.street')}
						error={errors['address.street']}
						touched={touched['address.street']}
						disabled={isLoading}
					/>

					<FormField
						label="Número"
						icon={<SignpostIcon />}
						value={formData.address.number ?? ''}
						onChange={(value) => onChange('address.number', value)}
						onBlur={() => onBlur('address.number')}
						error={errors['address.number']}
						touched={touched['address.number']}
						disabled={isLoading}
					/>
				</div>
			</div>
		</div>
	);
};
